/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/tenant-admin/BranchGeofenceModal.tsx
 * PURPOSE: SC-028-BRANCH-GEOFENCE modal [6] "Cấu hình vị trí (Geofence)" — dùng
 *          CHUNG cho cả 2 điểm vào (Điểm vào 1: menu ⁝ trên node cấp Chi nhánh
 *          của SC-006-ORG-STRUCTURE; Điểm vào 2: menu ⁝ trên 1 dòng bảng của
 *          tab "Vị trí chi nhánh" trong SC-003-SETTINGS-HUB). Gọi 1 endpoint
 *          duy nhất PUT /organizations/{org_id}/geofence dù mở từ đâu.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-12
 * COMPLIANCE: SDLC 6.5.0 + Zero Mock Tolerance — nối API THẬT /organizations/{org_id}/geofence
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs
 *
 * @business-context
 * - Domain: Tenant Administration, Branch GPS geofence configuration
 * - Impact: Unblocks ISS-PILOT-6 / BR-TENM-006 FR-012.v0 — Tenant Admin has no UI
 *   today to set per-branch GPS geofence (lat/lng/radius) for Employee clock-in.
 * - Stakeholders: Tenant Admins
 *
 * @technical-architecture
 * - Layer: Presentation Layer (shared modal component, mounted by 2 hosts)
 * - Pattern: apiClient (default /api/v1 baseURL), inline styles, token var(--nqh-*)
 *   — mirrors UnitTypesTab.tsx modal pattern.
 * - Dependencies: React 18, apiClient, backend/app/api/v1/organization.py
 *   (get_branch_geofence / update_branch_geofence)
 *
 * @cross-references
 * - Architecture: docs/02-design/18-adrs/06-decisions/DD-0099-branch-geofence-org-bridge-v0.md
 * - Requirement: docs/01-planning/01-requirements/03-tenant-management/BR-TENM-006-TENANT-SETTINGS.md (FR-012.v0)
 * - Wireframe: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §2 SC-028-BRANCH-GEOFENCE (modal [6], annotation block)
 * - Hosts: frontend/src/pages/tenant-admin/BranchGeofenceTab.tsx (Điểm vào 2)
 *   frontend/src/pages/OrganizationManagement/index.tsx (Điểm vào 1, ⁝ menu SC-006)
 */

import React, { useEffect, useState } from "react";
import { apiClient } from "../../services/api/client";

export interface BranchGeofenceTarget {
  orgId: string;
  name: string;
  code: string;
}

export interface BranchGeofenceSaved {
  orgId: string;
  configured: boolean;
  latitude: number | null;
  longitude: number | null;
  geofence_radius: number;
}

interface BranchGeofenceModalProps {
  target: BranchGeofenceTarget | null;
  onClose: () => void;
  onSaved: (result: BranchGeofenceSaved) => void;
}

const DEFAULT_RADIUS = "100";

const st = {
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: "white",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "480px",
    width: "90%",
  } as React.CSSProperties,
  modalTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
  } as React.CSSProperties,
  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
    color: "var(--nqh-gray-500, #6b7280)",
    padding: "2px",
  } as React.CSSProperties,
  bannerError: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "var(--nqh-chili-050, #fef2f2)",
    border: "1px solid var(--nqh-chili-200, #fecaca)",
    borderRadius: "8px",
    padding: "10px 12px",
    marginTop: "16px",
    marginBottom: "0",
    color: "var(--nqh-chili, #dc2626)",
    fontSize: "14px",
  } as React.CSSProperties,
  field: { marginTop: "16px" } as React.CSSProperties,
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: 500,
    color: "var(--nqh-gray-700, #374151)",
    marginBottom: "6px",
  } as React.CSSProperties,
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid var(--nqh-gray-300, #d1d5db)",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box" as const,
  } as React.CSSProperties,
  readOnlyInput: {
    background: "var(--nqh-gray-100, #f3f4f6)",
    color: "var(--nqh-gray-700, #374151)",
  } as React.CSSProperties,
  hint: {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    marginTop: "12px",
    fontSize: "12px",
    color: "var(--nqh-gray-500, #6b7280)",
  } as React.CSSProperties,
  modalFoot: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "20px",
  } as React.CSSProperties,
  btnPrimary: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    color: "white",
    background: "var(--nqh-coffee-500, #d97706)",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  } as React.CSSProperties,
  btnSecondary: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    color: "var(--nqh-gray-700, #374151)",
    background: "var(--nqh-gray-200, #e5e7eb)",
  } as React.CSSProperties,
  spinner: {
    display: "inline-block",
    width: "14px",
    height: "14px",
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "nqh-gf-spin 0.8s linear infinite",
  } as React.CSSProperties,
};

/**
 * Modal [6] "Cấu hình vị trí (Geofence)" — registry §SC-028. Luôn GET lại
 * /organizations/{org_id}/geofence khi mở (dù mở từ điểm vào nào) để đảm bảo
 * prefill đúng trạng thái hiện tại, tránh lệch dữ liệu giữa 2 điểm vào.
 */
export const BranchGeofenceModal: React.FC<BranchGeofenceModalProps> = ({ target, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [banner, setBanner] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setBanner(null);
    setLoading(true);
    apiClient
      .get(`/organizations/${target.orgId}/geofence`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        setLatitude(d.latitude != null ? String(d.latitude) : "");
        setLongitude(d.longitude != null ? String(d.longitude) : "");
        setRadius(String(d.geofence_radius ?? DEFAULT_RADIUS));
      })
      .catch(() => {
        if (!cancelled) setBanner("Không tải được cấu hình vị trí. Thử lại");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.orgId]);

  if (!target) return null;

  const handleSave = async () => {
    const hasLat = latitude.trim() !== "";
    const hasLng = longitude.trim() !== "";
    setBanner(null);

    // VALIDATE (registry §SC-028) — client-side mirror của validate backend,
    // cùng thông điệp VI để hiện ngay không cần round-trip khi rõ ràng sai.
    if (hasLat !== hasLng) {
      setBanner("Nhập đủ cả Vĩ độ và Kinh độ, hoặc để trống cả hai");
      return;
    }
    const latNum = hasLat ? Number(latitude) : null;
    const lngNum = hasLng ? Number(longitude) : null;
    if (hasLat && (latNum === null || Number.isNaN(latNum) || latNum < 8.0 || latNum > 24.0)) {
      setBanner("Vĩ độ phải trong khoảng 8.0–24.0");
      return;
    }
    if (hasLng && (lngNum === null || Number.isNaN(lngNum) || lngNum < 102.0 || lngNum > 110.0)) {
      setBanner("Kinh độ phải trong khoảng 102.0–110.0");
      return;
    }
    const radiusNum = Number(radius);
    if (!Number.isInteger(radiusNum) || radiusNum < 20 || radiusNum > 1000) {
      setBanner("Bán kính phải là số nguyên trong khoảng 20–1000 mét");
      return;
    }

    setSaving(true);
    try {
      const res = await apiClient.put(`/organizations/${target.orgId}/geofence`, {
        latitude: latNum,
        longitude: lngNum,
        geofence_radius: radiusNum,
      });
      // SAVED (registry §SC-028): đóng modal + báo caller làm mới dòng/bảng + toast.
      onSaved({ orgId: target.orgId, ...res.data });
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      setBanner(typeof msg === "string" ? msg : "Lưu không thành công. Thử lại");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={st.overlay} onClick={() => !saving && onClose()}>
      {/* keyframes cho spinner */}
      <style>{`@keyframes nqh-gf-spin { to { transform: rotate(360deg); } }`}</style>
      <div
        style={st.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="geofenceModalTitle"
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <h2 style={st.modalTitle} id="geofenceModalTitle">
            Cấu hình vị trí (Geofence)
          </h2>
          <button type="button" aria-label="Đóng" disabled={saving} onClick={onClose} style={st.closeBtn}>
            ✕
          </button>
        </div>

        {/* [6b] banner lỗi — ẩn khi rỗng */}
        {banner && (
          <div style={st.bannerError} role="alert" data-testid="geofence-banner">
            ⚠ {banner}
          </div>
        )}

        <div style={st.field}>
          <label style={st.label}>Chi nhánh</label>
          <input
            style={{ ...st.input, ...st.readOnlyInput }}
            value={`${target.name} · ${target.code}`}
            disabled
            readOnly
          />
        </div>

        <div style={st.field}>
          <label style={st.label} htmlFor="gfLat">
            Vĩ độ (Latitude) *
          </label>
          <input
            id="gfLat"
            style={st.input}
            type="number"
            step="any"
            value={latitude}
            disabled={loading || saving}
            onChange={(e) => setLatitude(e.target.value)}
          />
        </div>

        <div style={st.field}>
          <label style={st.label} htmlFor="gfLng">
            Kinh độ (Longitude) *
          </label>
          <input
            id="gfLng"
            style={st.input}
            type="number"
            step="any"
            value={longitude}
            disabled={loading || saving}
            onChange={(e) => setLongitude(e.target.value)}
          />
        </div>

        <div style={st.field}>
          <label style={st.label} htmlFor="gfRadius">
            Bán kính GPS hợp lệ (m) *
          </label>
          <input
            id="gfRadius"
            style={st.input}
            type="number"
            step="1"
            value={radius}
            disabled={loading || saving}
            onChange={(e) => setRadius(e.target.value)}
          />
        </div>

        {/* [6c] gợi ý tĩnh — KHÔNG phải lỗi, luôn hiển thị */}
        <p style={st.hint}>
          <span aria-hidden="true">ℹ</span>
          <span>Để trống cả Vĩ độ và Kinh độ để tắt giới hạn GPS cho chi nhánh này.</span>
        </p>

        <div style={st.modalFoot}>
          <button type="button" style={st.btnSecondary} disabled={saving} onClick={onClose}>
            Hủy
          </button>
          <button type="button" style={st.btnPrimary} disabled={saving || loading} onClick={handleSave}>
            {saving && <span style={st.spinner} aria-hidden="true" />}
            Lưu
          </button>
        </div>
      </div>
    </div>
  );
};

export default BranchGeofenceModal;
